/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Unified SplitModelSetSlideTable Component
 * Consolidates multiple ModelSetsTable implementations into a single, optimized component
 */

import React, { useMemo, useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGrid,
  type GridColDef,
  type GridRenderEditCellParams,
  type GridValueFormatter,
  useGridApiContext,
} from '@mui/x-data-grid';
import {
  getHoldingCategoryId,
  getHoldingCategoryOrder,
  holdingsCategories,
} from 'pages/Cases/helpers/misc';
import {
  SplitModelSetSlideTableProps,
  ModelSetTableRow,
  PortfolioOptionTableData,
} from './types';
import { parseModelSetData, validateUnifiedData } from './parsers/DataParser';

// Main component
const SplitModelSetSlideTable: React.FC<SplitModelSetSlideTableProps> = ({
  data,
  dataType = 'auto',
  modelSetName,
  slideMode = false,
  fontSize = 13,
  maxHeight,
  enableCollapse = false,
  columnHeaderHeight = 36,
  groupHeaderHeight,
  rowHeight,
  onChangeSlide,
}) => {
  // Parse data to unified format
  const {
    success,
    data: unifiedData,
    error,
  } = useMemo(() => {
    try {
      return parseModelSetData(data, dataType);
    } catch (error) {
      console.error('Error parsing data: ', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [data, dataType]);

  // Handle parsing errors
  if (!success || !unifiedData) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="body2">
          Error parsing data: {error || 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  // Validate unified data
  if (!validateUnifiedData(unifiedData)) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="body2">Invalid data structure detected</Typography>
      </Box>
    );
  }

  // Generate separate datasets for each portfolio option
  const portfolioOptionTables = useMemo(() => {
    try {
      return generatePortfolioOptionTables(unifiedData);
    } catch (error) {
      console.error('Error generating portfolio option tables: ', error);
      return [];
    }
  }, [unifiedData]);

  // Handle slide mode changes
  useEffect(() => {
    if (slideMode && onChangeSlide) {
      onChangeSlide({
        rows: portfolioOptionTables.flatMap(table => table.rows),
        columns: getColumnsForPortfolioOption(
          portfolioOptionTables[0]?.option || 1,
          portfolioOptionTables[0]?.portfolios || [],
          slideMode,
          fontSize,
          portfolioOptionTables[0]?.rows || [],
          dataType,
          fontSize, // Use original fontSize for strategist in this context
        ),
      });
    }
  }, [slideMode, unifiedData, onChangeSlide, portfolioOptionTables, fontSize]);

  return (
    <Grid
      id={`model-set-slide-table-${modelSetName}`}
      item
      xs={12}
      sx={{
        paddingBottom: '16px',
        '& .MuiGrid-container': {
          alignItems: 'stretch',
        },
      }}
    >
      {portfolioOptionTables.length === 1 ? (
        // Single table - full width
        <PortfolioOptionTable
          key={`portfolio-option-${portfolioOptionTables[0].option}`}
          tableData={portfolioOptionTables[0]}
          modelSetName={modelSetName}
          fontSize={fontSize}
          slideMode={slideMode}
          enableCollapse={enableCollapse}
          columnHeaderHeight={columnHeaderHeight}
          groupHeaderHeight={groupHeaderHeight}
          rowHeight={rowHeight}
          maxHeight={maxHeight}
          isLastTable={true}
          dataType={dataType}
          portfolioOptionTables={portfolioOptionTables}
        />
      ) : (
        // Multiple tables - side by side
        <Grid container spacing={1.5}>
          {portfolioOptionTables.map((tableData, index) => (
            <Grid
              item
              xs={12}
              lg={6}
              key={`portfolio-option-${tableData.option}`}
              sx={{ maxWidth: '450px' }} // Constrain width for side-by-side tables
            >
              <PortfolioOptionTable
                tableData={tableData}
                modelSetName={modelSetName}
                fontSize={fontSize}
                slideMode={slideMode}
                enableCollapse={enableCollapse}
                columnHeaderHeight={columnHeaderHeight}
                groupHeaderHeight={groupHeaderHeight}
                rowHeight={rowHeight}
                maxHeight={maxHeight}
                isLastTable={index === portfolioOptionTables.length - 1}
                dataType={dataType}
                portfolioOptionTables={portfolioOptionTables}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};

// Individual portfolio option table component
interface PortfolioOptionTableProps {
  tableData: PortfolioOptionTableData;
  modelSetName: string;
  fontSize: number;
  slideMode: boolean;
  enableCollapse: boolean;
  columnHeaderHeight: number;
  groupHeaderHeight?: number;
  rowHeight?: number;
  maxHeight?: number;
  isLastTable?: boolean;
  dataType?: string;
  portfolioOptionTables?: PortfolioOptionTableData[];
}

const PortfolioOptionTable: React.FC<PortfolioOptionTableProps> = ({
  tableData,
  modelSetName,
  fontSize,
  slideMode,
  enableCollapse,
  columnHeaderHeight,
  groupHeaderHeight,
  maxHeight,
  isLastTable, // eslint-disable-line @typescript-eslint/no-unused-vars
  dataType = 'auto',
  portfolioOptionTables = [],
}) => {
  // console.log('PortfolioOptionTable : ', tableData);
  const { option, portfolios, rows } = tableData;

  // Calculate strategy count for this specific option
  const strategyCount = useMemo(() => {
    const uniqueStrategies = new Set<string>();
    rows.forEach(row => {
      uniqueStrategies.add(
        JSON.stringify({ strategy: row.strategy, profile: row.profile }),
      );
    });
    return uniqueStrategies.size;
  }, [rows]);

  // Group header height calculation
  const calculatedGroupHeaderHeight = useMemo(() => {
    if (groupHeaderHeight) return groupHeaderHeight;
    // if (rows.length > 10 && rows.length <= 14) return 50;
    // if (rows.length > 14) return 40;
    return 40;
  }, [rows.length, groupHeaderHeight]);

  // Optimized calculations - combine all related computations
  const {
    cellPaddingX,
    cellPaddingY,
    compactFontSize,
    strategistFontSize,
    columns,
    columnGroupingModel,
    rowHeight,
  } = useMemo(() => {
    try {
      const reportType = dataType || 'auto';
      const isSideBySide = portfolioOptionTables.length > 1;
      const groupHeaderHeight = calculatedGroupHeaderHeight;

      // Use auto-adjustment function to calculate optimal dimensions
      const optimalDimensions = calculateOptimalDimensions(
        rows,
        reportType,
        isSideBySide,
        groupHeaderHeight,
        portfolios.length,
      );

      // console.log('SplitModelSetSlideTable Padding Calculation:', {
      //   portfoliosCount: portfolios.length,
      //   isSideBySide,
      //   dataType,
      //   cellPaddingX: optimalDimensions.cellPaddingX,
      //   cellPaddingY: optimalDimensions.cellPaddingY,
      //   headerPadding: `Y:${optimalDimensions.cellPaddingY}px X:${optimalDimensions.cellPaddingX}px`,
      //   baseColumnsHeaderPadding: `base-columns-header will use Y:${optimalDimensions.cellPaddingY}px X:${optimalDimensions.cellPaddingX}px`,
      // });

      // Generate columns and grouping model
      const columns = getColumnsForPortfolioOption(
        option,
        portfolios,
        slideMode,
        fontSize,
        rows,
        dataType,
        optimalDimensions.strategistFontSize,
      );

      const columnGroupingModel = getColumnGroupingModelForPortfolioOption(
        option,
        portfolios,
        modelSetName,
        optimalDimensions.cellPaddingX,
        optimalDimensions.cellPaddingY,
      );

      return {
        cellPaddingX: optimalDimensions.cellPaddingX,
        cellPaddingY: optimalDimensions.cellPaddingY,
        compactFontSize: optimalDimensions.fontSize,
        strategistFontSize: optimalDimensions.strategistFontSize,
        columns,
        columnGroupingModel,
        rowHeight: optimalDimensions.rowHeight,
      };
    } catch (error) {
      console.error('Error calculating optimal dimensions: ', error);
      return {
        cellPaddingX: CONSTANTS.MIN_CELL_PADDING,
        cellPaddingY: CONSTANTS.MIN_CELL_PADDING,
        compactFontSize: CONSTANTS.MIN_FONT_SIZE,
        strategistFontSize: CONSTANTS.MIN_FONT_SIZE,
        columns: [],
        columnGroupingModel: [],
        rowHeight: CONSTANTS.MIN_ROW_HEIGHT,
      };
    }
  }, [
    dataType,
    rows,
    strategyCount,
    calculatedGroupHeaderHeight,
    portfolioOptionTables.length,
    option,
    portfolios,
    slideMode,
    modelSetName,
    fontSize,
  ]);

  return (
    <Box sx={{ width: '100%', maxHeight, maxWidth: '100%', overflow: 'hidden' }}>
      <StyledDataGrid
        hideFooter
        disableColumnMenu
        disableColumnResize
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        columnHeaderHeight={columnHeaderHeight}
        columnGroupHeaderHeight={calculatedGroupHeaderHeight}
        disableRowSelectionOnClick
        getRowHeight={() => rowHeight > CONSTANTS.BASE_ROW_HEIGHT ? rowHeight : 'auto'}
        getCellClassName={params => `${getCellClassName(params)}`}
        sx={{
          height: 'auto',
          '& .MuiDataGrid-row': {
            ...(rowHeight > CONSTANTS.BASE_ROW_HEIGHT && {
              minHeight: `${rowHeight}px !important`,
              maxHeight: `${rowHeight}px !important`,
            }),
          },
          '& .MuiDataGrid-cell': {
            fontSize: `${compactFontSize}px`,
            padding: `${cellPaddingY}px ${cellPaddingX}px`,
            ...(rowHeight > CONSTANTS.BASE_ROW_HEIGHT && {
              minHeight: `${rowHeight}px`,
              maxHeight: `${rowHeight}px`,
            }),
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.2,
          },
          '& .MuiDataGrid-cell[data-field^="option"]': {
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            fontSize: `${Math.max(CONSTANTS.MIN_FONT_SIZE, compactFontSize)}px`,
            ...(rowHeight > CONSTANTS.BASE_ROW_HEIGHT && {
              minHeight: `${rowHeight}px`,
              maxHeight: `${rowHeight}px`,
            }),
          },
          '& .MuiDataGrid-cell[data-field^="strategy"]': {
            // paddingLeft: `${cellPaddingX}px`,
            // paddingRight: `${cellPaddingX}px`,
            paddingLeft: '8px',
            paddingRight: '8px',
            textAlign: 'left',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            fontSize: `${strategistFontSize}px`,
            ...(rowHeight > CONSTANTS.BASE_ROW_HEIGHT && {
              minHeight: `${rowHeight}px`,
              maxHeight: `${rowHeight}px`,
            }),
          },
          '& .MuiDataGrid-cell[data-field^="profile"]': {
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            fontSize: `${Math.max(CONSTANTS.MIN_FONT_SIZE, compactFontSize)}px`,
            minHeight: `${CONSTANTS.MIN_ROW_HEIGHT}px`,
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            fontSize: `${Math.max(CONSTANTS.MIN_FONT_SIZE, compactFontSize)}px`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // padding: `${cellPaddingY}px ${cellPaddingX}px`,
          },
          // Apply padding specifically to base-columns-header
          '& .MuiDataGrid-columnHeader.base-columns-header': {
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              // padding: `${cellPaddingY}px ${cellPaddingX}px !important`,
            },
            // Also apply padding to the header cell itself
            padding: `${cellPaddingY}px ${cellPaddingX}px !important`,
          },
          // Additional styling for better text handling
          '& .MuiDataGrid-columnHeader[data-field^="strategy"]': {
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              whiteSpace: 'normal',
              lineHeight: 1.1,
              // padding: `${cellPaddingY}px ${cellPaddingX}px`,
            },
          },
        }}
      />
    </Box>
  );
};

// Constants and helper functions
const CONSTANTS = {
  MAX_ROWS_BEFORE_COMPACT: 10,
  MAX_TABLE_HEIGHT: 534,
  BASE_ROW_HEIGHT: 20 as number,
  MARGIN_HEIGHT: 20,
  MIN_FONT_SIZE: 10,
  MIN_ROW_HEIGHT: 20 as number,
  MIN_CELL_PADDING: 2,
  FONT_MULTIPLIERS: {
    'performance-report': 0.75,
    'premium-report': 0.85,
    cases: 1.0,
    auto: 0.8,
  },
  STRATEGIST_MULTIPLIERS: {
    'performance-report': 0.85,
    'premium-report': 0.9,
    cases: 1.0,
    auto: 0.9,
  },
  AUTO_ADJUSTMENT_FACTORS: {
    FONT_SIZE_REDUCTION: 0.9,
    PADDING_REDUCTION: 0.85,
    ROW_HEIGHT_REDUCTION: 0.9,
    MAX_ITERATIONS: 3,
  },
} as const;

// Calculate cell padding for X and Y axes separately
const calculateCellPadding = (
  isSideBySide: boolean,
  portfoliosCount: number,
  dataType: string,
  avgStrategyLength: number,
) => {
  try {
    // Base padding values
    let paddingX = 10; // Horizontal padding
    let paddingY = 10; // Vertical padding

    // Apply side-by-side multiplier for horizontal space management
    if (isSideBySide) {
      // More aggressive reduction for horizontal space
      paddingX *= 0.3; // Much smaller horizontal padding
      paddingY *= 0.8; // Moderate vertical padding reduction
    }

    // Adjust based on number of portfolios (more columns = less horizontal space)
    if (portfoliosCount > 5 && isSideBySide) {
      // paddingX *= 0.5; // Further reduce horizontal padding
      paddingX *= 0.1;
    } else if (portfoliosCount > 3) {
      paddingX *= 0.7; // Moderate horizontal padding reduction
    }

    // Adjust based on report type
    const isPerformanceReport = dataType === 'performance-report';
    const isPremiumReport = dataType === 'premium-report';

    if (isPerformanceReport) {
      paddingX *= 0.8;
      paddingY *= 0.9;
    } else if (isPremiumReport) {
      paddingX *= 0.9;
      paddingY *= 0.95;
    }
    // Adjust based on content length
    if (avgStrategyLength > 50) {
      paddingX *= 0.8; // Reduce horizontal padding for long names
    } else if (avgStrategyLength > 30) {
      paddingX *= 0.9;
    }

    // if (strategyCount > 14) {
    //   paddingY *= 0.7;
    // }

    // Ensure minimum values
    paddingX = Math.max(paddingX, CONSTANTS.MIN_CELL_PADDING);
    paddingY = Math.max(paddingY, CONSTANTS.MIN_CELL_PADDING);

    return {
      paddingX: Math.round(paddingX),
      paddingY: Math.round(paddingY),
    };
  } catch (error) {
    console.error('Error calculating cell padding: ', error);
    return { paddingX: CONSTANTS.MIN_CELL_PADDING, paddingY: CONSTANTS.MIN_CELL_PADDING };
  }
};

// Auto-adjustment function to prevent table overflow - only when needed
const calculateOptimalDimensions = (
  rows: ModelSetTableRow[],
  dataType: string,
  isSideBySide: boolean,
  groupHeaderHeight: number,
  portfoliosCount: number,
) => {
  try {
    let fontSize = 12;
    let rowHeight = CONSTANTS.BASE_ROW_HEIGHT;

    // Calculate content characteristics
    const avgStrategyLength = rows.reduce((sum, row) => {
      return sum + (row.strategy?.length || 0);
    }, 0) / Math.max(rows.length, 1);

    // Calculate strategy count for this specific option
    const uniqueStrategies = new Set<string>();
    rows.forEach(row => {
      uniqueStrategies.add(
        JSON.stringify({ strategy: row.strategy, profile: row.profile }),
      );
    });
    const strategyCount = uniqueStrategies.size;

    // Calculate separate padding for X and Y
    let { paddingX, paddingY } = calculateCellPadding(
      isSideBySide,
      portfoliosCount,
      dataType,
      avgStrategyLength,
    );

    // Apply initial multipliers based on report type
    const fontMultiplier = CONSTANTS.FONT_MULTIPLIERS[dataType as keyof typeof CONSTANTS.FONT_MULTIPLIERS] || 1.0;
    fontSize *= fontMultiplier;

    // Apply side-by-side multiplier
    if (isSideBySide) {
      fontSize *= 0.9;
    }

    // Content-based adjustments for long strategy names (always apply)
    if (avgStrategyLength > 50) {
      fontSize *= 0.9;
    } else if (avgStrategyLength > 30) {
      fontSize *= 0.95;
    }

    // Check if table would overflow with current settings
    const estimatedTableHeight =
      groupHeaderHeight +
      (rows.length * (rowHeight + paddingY * 2)) +
      CONSTANTS.MARGIN_HEIGHT;

    // Only apply auto-adjustment if table would actually overflow
    if (estimatedTableHeight > CONSTANTS.MAX_TABLE_HEIGHT) {
      const overflowAmount = estimatedTableHeight - CONSTANTS.MAX_TABLE_HEIGHT;
      const overflowRatio = overflowAmount / CONSTANTS.MAX_TABLE_HEIGHT;

      // Different strategies based on overflow severity
      if (overflowRatio <= 0.2) {
        // Mild overflow (≤20%) - gentle reduction
        fontSize *= 0.9;
        rowHeight *= 0.9;
      } else if (overflowRatio <= 0.5) {
        // Moderate overflow (20-50%) - moderate reduction
        fontSize *= 0.8;
        rowHeight *= 0.85;
      } else {
        // Severe overflow (>50%) - more aggressive but still conservative
        fontSize *= 0.75;
        rowHeight *= 0.8;
      }

      // Ensure minimum values
      fontSize = Math.max(fontSize, CONSTANTS.MIN_FONT_SIZE);
      rowHeight = Math.max(rowHeight, CONSTANTS.MIN_ROW_HEIGHT);

      // If still overflowing, apply gentle iterative adjustment
      let currentEstimatedHeight =
        groupHeaderHeight +
        (rows.length * (rowHeight + paddingY * 2)) +
        CONSTANTS.MARGIN_HEIGHT;

      for (let iteration = 0; iteration < CONSTANTS.AUTO_ADJUSTMENT_FACTORS.MAX_ITERATIONS; iteration++) {
        if (currentEstimatedHeight <= CONSTANTS.MAX_TABLE_HEIGHT) {
          break; // Table fits within constraints
        }

        // Apply gentle reductions (smaller steps)
        fontSize *= 0.95; // More gentle than 0.9
        rowHeight *= 0.95; // More gentle than 0.9

        // Ensure minimum values
        fontSize = Math.max(fontSize, CONSTANTS.MIN_FONT_SIZE);
        rowHeight = Math.max(rowHeight, CONSTANTS.MIN_ROW_HEIGHT);

        // Apply padding reduction for strategy count
        if (strategyCount > 14) {
          rowHeight *= 0.7;
          paddingY *= 0.7;
          paddingX = paddingX * 1;
        }


        // Recalculate height
        currentEstimatedHeight =
          groupHeaderHeight +
          (rows.length * (rowHeight + paddingY * 2)) +
          CONSTANTS.MARGIN_HEIGHT;
      }
    }

    // Final minimum constraints
    fontSize = Math.max(fontSize, CONSTANTS.MIN_FONT_SIZE);
    rowHeight = Math.max(rowHeight, CONSTANTS.MIN_ROW_HEIGHT);

    const finalDimensions = {
      fontSize: Math.round(fontSize * 10) / 10, // Round to 1 decimal
      cellPaddingX: paddingX,
      cellPaddingY: paddingY,
      rowHeight: Math.round(rowHeight),
      strategistFontSize: Math.round(fontSize * 1.1 * 10) / 10,
    };

    return finalDimensions;
  } catch (error) {
    console.error('Error calculating optimal dimensions: ', error);
    return {
      cellPaddingX: CONSTANTS.MIN_CELL_PADDING,
      cellPaddingY: CONSTANTS.MIN_CELL_PADDING,
      rowHeight: CONSTANTS.MIN_ROW_HEIGHT,
      strategistFontSize: CONSTANTS.MIN_FONT_SIZE,
      fontSize: CONSTANTS.MIN_FONT_SIZE,
    };
  }
};

const getOptionKey = (option: number, riskProfile: number) =>
  `option_${option}_riskprofile_${riskProfile}`;

const generatePortfolioOptionTables = (
  modelSet: any,
): PortfolioOptionTableData[] => {
  if (!modelSet?.portfolio_options) return [];

  return modelSet.portfolio_options.map((portfolioOptions: any) => {
    const { option, portfolios } = portfolioOptions;
    const uniqueStrategies = new Map<
      string,
      { strategy: string; profile: number; category_role: string }
    >();

    // Collect unique strategies more efficiently
    portfolios.forEach((portfolio: any) => {
      portfolio.holdings.forEach((holding: any) => {
        if (holding.weight > 0) {
          const key = `${holding.strategy}_${holding.profile}`;
          if (!uniqueStrategies.has(key)) {
            uniqueStrategies.set(key, {
              strategy: holding.strategy,
              profile: holding.profile,
              category_role: holding.category_role,
            });
          }
        }
      });
    });

    // Create rows array
    const rows: ModelSetTableRow[] = Array.from(uniqueStrategies.values()).map(
      ({ strategy, profile, category_role }, index) => ({
        id: index,
        strategy,
        profile,
        rowClassName: `category-${getHoldingCategoryId(category_role)}`,
        order: getHoldingCategoryOrder(category_role),
      }),
    );

    // Create a map for faster row lookup
    const rowMap = new Map<string, ModelSetTableRow>();
    rows.forEach(row => {
      rowMap.set(`${row.strategy}_${row.profile}`, row);
    });

    // Populate weights
    portfolios.forEach((portfolio: any) => {
      const weightKey = getOptionKey(option, portfolio.risk_profile);

      portfolio.holdings.forEach((holding: any) => {
        const row = rowMap.get(`${holding.strategy}_${holding.profile}`);
        if (row) {
          const weight = ensureWeight(holding.weight * 100);
          row[weightKey] = weight;
          row[`${weightKey}_prev`] = weight;
        }
      });

      // Fill missing weights with 0
      rows.forEach(row => {
        if (row[weightKey] === undefined) {
          row[weightKey] = 0;
          row[`${weightKey}_prev`] = 0;
        }
      });
    });

    return { option, portfolios, rows: sortRows(rows) };
  });
};

const getColumnsForPortfolioOption = (
  option: number,
  portfolios: any[],
  slideMode: boolean,
  fontSize: number,
  rows: ModelSetTableRow[],
  dataType: string = 'auto',
  strategistFontSize: number = fontSize,
): GridColDef[] => {
  // Pre-calculate column requirements
  const strategySet = new Set(rows.map(row => row.strategy));
  const profileSet = new Set(
    rows.map(row => row.profile).filter(p => p !== null),
  );
  const hasMultipleStrategies = strategySet.size > 1;
  const hasMultipleProfiles = profileSet.size > 1;

  const baseColumns: GridColDef[] = [];

  // Strategy column
  if (hasMultipleStrategies) {
    baseColumns.push({
      field: 'strategy',
      headerName: 'Strategist',
      sortable: false,
      headerClassName: 'base-columns-header',
      editable: false,
      ...getColumnWidth('strategy', dataType),
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            '&:hover .delete-button': { opacity: 1 },
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: `${strategistFontSize}px`,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.2,
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    });
  }

  // Profile column
  if (hasMultipleProfiles) {
    baseColumns.push({
      field: 'profile',
      headerName: 'Profile',
      sortable: false,
      headerClassName: 'base-columns-header',
      headerAlign: 'center' as const,
      align: 'center' as const,
      editable: false,
      type: 'number' as const,
      cellClassName: 'profile-column',
      ...getColumnWidth('profile', dataType),
    });
  }

  // Portfolio columns - sort by risk_profile in ascending order
  const portfolioColumns: GridColDef[] = portfolios
    .sort((a: any, b: any) => a.risk_profile - b.risk_profile)
    .map((portfolio: any, index: number) => {
      const field = getOptionKey(option, portfolio.risk_profile);
      const isLastColumn = index === portfolios.length - 1;

      return {
        field,
        headerName: portfolio.risk_profile.toString(),
        sortable: false,
        headerAlign: 'center' as const,
        align: 'center' as const,
        editable: false,
        type: 'number' as const,
        cellClassName: isLastColumn ? 'last-risk-profile-column' : '',
        ...getColumnWidth('', dataType),
        renderEditCell: (params: GridRenderEditCellParams) => {
          if (slideMode) return params.formattedValue;
          if (params.row[params.field] !== undefined) {
            return <CustomEditCell {...params} compactFontSize={fontSize} />;
          }
          return null;
        },
        valueFormatter: (weight: GridValueFormatter) =>
          weight === undefined || Number(weight) === 0
            ? ''
            : `${Number(weight)}%`,
        preProcessEditCellProps: (params: any) => {
          const value = params.props.value;
          const hasError = isNaN(value) || value < 0 || value > 100;
          return { ...params.props, error: hasError };
        },
      };
    });

  // Adjust portfolio column widths if no base columns
  if (baseColumns.length === 0) {
    const isPerformanceReport = dataType === 'performance-report';
    const isPremiumReport = dataType === 'premium-report';

    const widthConfig = isPerformanceReport
      ? { minWidth: 35, flex: 1 }
      : isPremiumReport
        ? { minWidth: 40, flex: 1 }
        : { minWidth: 50, flex: 1 };

    portfolioColumns.forEach(col => Object.assign(col, widthConfig));
  }

  return [...baseColumns, ...portfolioColumns];
};

const getColumnGroupingModelForPortfolioOption = (
  option: number,
  portfolios: any[],
  modelSetName: string,
  cellPaddingX: number,
  cellPaddingY: number,
) => [
  {
    groupId: `Option${option}`,
    headerName: `${modelSetName}.`,
    description: `Option ${option} - Client Risk Profile`,
    headerAlign: 'center' as const,
    renderHeaderGroup: (params: any) => <HeaderGroup {...params} cellPaddingX={cellPaddingX} cellPaddingY={cellPaddingY} />,
    children: portfolios
      .sort((a: any, b: any) => a.risk_profile - b.risk_profile)
      .map((portfolio: any) => ({
        field: getOptionKey(option, portfolio.risk_profile),
        headerName: portfolio.risk_profile.toString(),
        renderHeader: (_params: any) => portfolio.risk_profile.toString(),
      })),
  },
];

const getColumnWidth = (field: string, reportType: string = 'auto') => {
  // Adjust column widths based on report type
  const isPerformanceReport = reportType === 'performance-report';
  const isPremiumReport = reportType === 'premium-report';

  if (field === 'full_model_name' || field === 'strategy') {
    // Strategy column - adjust based on report type
    if (isPerformanceReport) {
      return { minWidth: 120, flex: 1.2 }; // Much smaller for performance reports
    } else if (isPremiumReport) {
      return { minWidth: 160, flex: 1.3 }; // Medium for premium reports
    } else {
      return { minWidth: 200, flex: 1.5 }; // Full size for cases
    }
  } else if (field === 'profile') {
    // Profile column - more compact for smaller reports
    if (isPerformanceReport) {
      return { minWidth: 35, flex: 0.15 }; // Very compact for performance reports
    } else if (isPremiumReport) {
      return { minWidth: 45, flex: 0.18 }; // Medium compact for premium reports
    } else {
      return { minWidth: 40, flex: 0.2 }; // Standard size for cases
    }
  } else {
    // Portfolio columns - adjust based on report type
    if (isPerformanceReport) {
      return { minWidth: 30, flex: 0.15 }; // Very compact for performance reports
    } else if (isPremiumReport) {
      return { minWidth: 35, flex: 0.18 }; // Medium compact for premium reports
    } else {
      return { minWidth: 45, flex: 0.25 }; // Standard size for cases
    }
  }
};

// const findRow = (rows: ModelSetTableRow[], strategy: string, profile: number) =>
//   rows.find(row => row.strategy === strategy && row.profile === profile);

const sortRows = (rows: ModelSetTableRow[]) => {
  rows.sort((a, b) => (a.profile ?? Infinity) - (b.profile ?? Infinity));
  rows.sort((a, b) => a.strategy.localeCompare(b.strategy));
  rows.sort((a, b) => a.order - b.order);
  return rows;
};

const ensureWeight = (value: number = 0, decimals: number = 2) => {
  const percentage = Math.max(0, Math.min(value, 100));
  return parseFloat(percentage.toFixed(decimals));
};

const getCellClassName = (params: any) => {
  let className = '';
  if (params.id === 'totals') {
    className = 'total-row';
    const value = params.row[params.field];
    if (value !== 100) {
      className += ' total-error';
    }
  } else {
    if (params.field.startsWith('option')) {
      className += ' input-cell';
    }
    className += ` ${params.row.rowClassName}`;
  }

  if (params.row[params.field] !== undefined) {
    className += ' weight-cell';
  }

  return className;
};

const CustomEditCell = React.memo((props: GridRenderEditCellParams) => {
  const { id, value, field, compactFontSize } = props;
  const api = useGridApiContext();

  const handleChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = ev.target.value === '' ? 0 : ev.target.valueAsNumber;
      api.current.setEditCellValue({
        id,
        field,
        value: ensureWeight(newValue),
      });
    },
    [api, id, field],
  );

  const inputValue = formatWeight(value as number).replace('%', '');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <input
        autoFocus
        type="number"
        min={0}
        max={100}
        value={inputValue}
        onChange={handleChange}
        style={{
          width: '35px',
          padding: '2px 1px',
          border: '1px solid #ddd',
          borderRadius: '3px',
          backgroundColor: '#fff',
          textAlign: 'center',
          fontSize: compactFontSize || '11px',
          fontWeight: 'bold',
          outline: 'none',
          fontFamily: 'Roboto',
        }}
      />
    </Box>
  );
});

const HeaderGroup = React.memo((params: any) => {
  const { cellPaddingX = 8, cellPaddingY = 8 } = params;

  return (
    <Box sx={{
      textAlign: 'center',
      width: '100%',
      padding: `${cellPaddingY}px ${cellPaddingX}px`,
    }}>
      <Typography
        variant="subtitle2"
        component="div"
        sx={{
          width: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          fontSize: '10px',
          textOverflow: 'ellipsis',
          lineHeight: 1.1,
          marginBottom: '2px',
        }}
      >
        {params.headerName}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{
          fontSize: '9px',
          lineHeight: 1.1,
        }}
      >
        {params.description}
      </Typography>
    </Box>
  );
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => {
  return {
    ...getCategoryClassNames(),
    borderTop: 'none',
    borderRight: 'none',
    '& *': {
      outline: 'none!important',
      cursor: 'default!important',
    },
    '& svg': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      color: '#666666',
      borderBottom: 'none!important',
    },
    '& .MuiDataGrid-columnHeader': {
      borderTop: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      '&[data-field^="option"]': {
        backgroundColor: theme.palette.background.paper,
      },
      // Ensure headers respect the padding constraints
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    // Specific styling for base-columns-header
    '& .MuiDataGrid-columnHeader.base-columns-header': {
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    '& .MuiDataGrid-columnHeader:first-of-type': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    // Group header styling
    '& .MuiDataGrid-columnGroupHeader': {
      '& .MuiDataGrid-columnGroupHeaderContent': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    '& .MuiDataGrid-cell': {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiDataGrid-cell:first-of-type': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '& .profile-column, & .last-risk-profile-column': {
      borderRightWidth: '3px',
    },
    '& .last-risk-profile-column': {
      borderRightWidth: '3px',
    },
    '& input[type="number"]': {
      MozAppearance: 'textfield',
    },
  };
});

const getCategoryClassNames = () =>
  holdingsCategories.reduce((css: any, category) => {
    css[`& .category-${category.id}`] = {
      backgroundColor: `${category.color} !important;`,
    };
    return css;
  }, {});

const formatWeight = (weight: number | undefined): string => {
  return weight === undefined || Number(weight) === 0
    ? ''
    : `${Number(weight)}%`;
};

export default SplitModelSetSlideTable;
